import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { formatDate } from "@ckgo2/shared/utils";
import {
  TRIP_DATE_LIFECYCLES,
  TRIP_DATE_LIFECYCLE_LABELS,
  TRIP_DATE_LIFECYCLE_BADGES,
  type TripDateLifecycle,
  type OccupancyLevel,
} from "@ckgo2/shared/types";
import { LayoutGrid, Plane, ChevronRight, AlertTriangle } from "lucide-react";

interface FlightSignal {
  airline: string;
  used: number;
  seatsTotal: number;
  nextDeadline: "deposit" | "foc" | "ticketing" | null;
  dueDate: string | null;
  daysTo: number | null;
  severity: "ok" | "warn" | "urgent";
  atRisk: number | null;
  currency: string;
  blockCount: number;
}

interface SeasonRow {
  id: string;
  tripId: string;
  code: string | null;
  startDate: string;
  endDate: string;
  month: string;
  capacity: number;
  bookedCount: number;
  waitlistCount: number;
  paxBooked: number;
  orderCount: number;
  occupancy: OccupancyLevel;
  fillPct: number;
  lifecycle: TripDateLifecycle;
  minPax: number | null;
  pnr: string | null;
  flightSignal: FlightSignal | null;
  tripTitle?: string | null;
  destinationId?: string | null;
  destinationName?: string | null;
  guideName?: string | null;
  garant?: { id: string; name: string; source: "departure" | "destination" } | null;
  nearestDeadline: string | null;
  goNoGo: { level: "ok" | "watch" | "risk" | "off"; label: string };
}

// Occupancy → fill-bar colour (solid; the badge palette is for chips).
const FILL_COLOR: Record<OccupancyLevel, string> = {
  open: "bg-blue-500",
  few_spots: "bg-amber-500",
  full: "bg-gray-400",
};

const GO_NO_GO: Record<SeasonRow["goNoGo"]["level"], string> = {
  ok: "bg-emerald-100 text-emerald-700",
  watch: "bg-amber-100 text-amber-700",
  risk: "bg-red-100 text-red-700",
  off: "bg-gray-100 text-gray-500",
};

// Flight allotment chip (Block 4.10 / phase 2) — airline + drawdown always; the deadline badge is
// coloured only when at risk (warn/urgent), so the board doesn't shout about a FOC 40 days out.
const DEADLINE_SHORT: Record<"deposit" | "foc" | "ticketing", string> = {
  deposit: "Deposit",
  foc: "FOC",
  ticketing: "Ticket",
};
const FLIGHT_SEV: Record<"ok" | "warn" | "urgent", string> = {
  ok: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300",
  warn: "bg-amber-100 text-amber-700",
  urgent: "bg-red-100 text-red-700",
};

function flightCountdown(days: number | null): string {
  if (days == null) return "";
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "today";
  return `${days}d`;
}

function FlightChip({ s }: { s: FlightSignal }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
      <Plane className="h-3 w-3 text-gray-400" />
      <span className="font-medium">{s.airline}</span>
      <span className="tabular-nums">
        {s.used}/{s.seatsTotal}
      </span>
      {s.nextDeadline && (
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${FLIGHT_SEV[s.severity]}`}
          title={
            s.atRisk != null
              ? `${s.atRisk.toLocaleString("en-US")} ${s.currency} at risk on unsold seats`
              : undefined
          }
        >
          {DEADLINE_SHORT[s.nextDeadline]} {flightCountdown(s.daysTo)}
        </span>
      )}
      {s.blockCount > 1 && <span className="text-gray-400">+{s.blockCount - 1}</span>}
    </span>
  );
}

function monthLabel(month: string): string {
  const [y, m] = month.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

export default function SeasonPage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const fDest = params.get("dest") || "";
  const fGarant = params.get("garant") || "";
  const fLifecycle = params.get("lifecycle") || "";

  const { data, isLoading } = useQuery({
    queryKey: ["season"],
    queryFn: () => api.get<{ data: SeasonRow[] }>("/api/admin/season"),
  });
  const { data: usersData } = useQuery({
    queryKey: ["users", "lookup"],
    queryFn: () => api.get<{ data: { id: string; name: string }[] }>("/api/admin/users?limit=100"),
  });
  const rows = data?.data ?? [];
  const users = usersData?.data ?? [];

  const save = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.patch(`/api/admin/departures/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["season"] }),
  });

  // Filter option lists derived from the board itself (no extra fetch).
  const destOptions = useMemo(() => {
    const m = new Map<string, string>();
    rows.forEach((r) => r.destinationId && m.set(r.destinationId, r.destinationName ?? "—"));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);
  const garantOptions = useMemo(() => {
    const m = new Map<string, string>();
    rows.forEach((r) => r.garant && m.set(r.garant.id, r.garant.name));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const filtered = rows.filter(
    (r) =>
      (!fDest || r.destinationId === fDest) &&
      (!fGarant || r.garant?.id === fGarant) &&
      (!fLifecycle || r.lifecycle === fLifecycle),
  );

  // Group by month (stable — rows arrive ordered by startDate).
  const groups = useMemo(() => {
    const g = new Map<string, SeasonRow[]>();
    filtered.forEach((r) => {
      const arr = g.get(r.month) ?? [];
      arr.push(r);
      g.set(r.month, arr);
    });
    return [...g.entries()];
  }, [filtered]);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-white">
            <LayoutGrid className="h-6 w-6" /> Season
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            The whole selling quarter at a glance — codes, garant, fill & go/no-go.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            className="input h-9 w-auto py-1 text-sm"
            value={fDest}
            onChange={(e) => setFilter("dest", e.target.value)}
          >
            <option value="">All destinations</option>
            {destOptions.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
          <select
            className="input h-9 w-auto py-1 text-sm"
            value={fGarant}
            onChange={(e) => setFilter("garant", e.target.value)}
          >
            <option value="">All garants</option>
            {garantOptions.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
          <select
            className="input h-9 w-auto py-1 text-sm"
            value={fLifecycle}
            onChange={(e) => setFilter("lifecycle", e.target.value)}
          >
            <option value="">All lifecycles</option>
            {TRIP_DATE_LIFECYCLES.map((lc) => (
              <option key={lc} value={lc}>
                {TRIP_DATE_LIFECYCLE_LABELS[lc]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-48 items-center justify-center text-gray-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="card flex h-48 flex-col items-center justify-center text-gray-500">
          <LayoutGrid className="mb-2 h-10 w-10 text-gray-300" />
          <p>No departures in this window</p>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map(([month, monthRows]) => (
            <div key={month} className="card overflow-hidden p-0">
              <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-200">
                {monthLabel(month)}{" "}
                <span className="font-normal text-gray-400">({monthRows.length})</span>
              </div>
              <table className="w-full">
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {monthRows.map((r) => (
                    <SeasonRowItem
                      key={r.id}
                      r={r}
                      users={users}
                      saving={save.isPending}
                      onPatch={(patch) => save.mutate({ id: r.id, patch })}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SeasonRowItem({
  r,
  users,
  saving,
  onPatch,
}: {
  r: SeasonRow;
  users: { id: string; name: string }[];
  saving: boolean;
  onPatch: (patch: Record<string, unknown>) => void;
}) {
  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
      {/* Code + trip */}
      <td className="px-4 py-3 align-top">
        {r.code && (
          <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
            {r.code}
          </span>
        )}
        <div className="mt-0.5 text-sm font-medium text-gray-900 dark:text-white">
          {r.tripTitle ?? "—"}
        </div>
        <div className="text-xs text-gray-400">{r.destinationName ?? ""}</div>
      </td>

      {/* Dates */}
      <td className="whitespace-nowrap px-3 py-3 align-top text-sm text-gray-700 dark:text-gray-300">
        {formatDate(r.startDate)}
        <div className="text-xs text-gray-400">– {formatDate(r.endDate)}</div>
      </td>

      {/* Garant (inline) */}
      <td className="px-3 py-3 align-top">
        <select
          className="input h-8 w-32 py-0 text-xs"
          value={r.garant?.source === "departure" ? r.garant.id : ""}
          disabled={saving}
          onChange={(e) => onPatch({ leadUserId: e.target.value || null })}
          title={
            r.garant?.source === "destination"
              ? `Inherited from destination: ${r.garant.name}`
              : undefined
          }
        >
          <option value="">
            {r.garant?.source === "destination" ? `↳ ${r.garant.name}` : "— none —"}
          </option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
      </td>

      {/* Fill bar (derived occupancy) + capacity inline */}
      <td className="px-3 py-3 align-top">
        <div className="flex items-center gap-2">
          <div className="h-2 w-24 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className={`h-full ${FILL_COLOR[r.occupancy]}`}
              style={{ width: `${Math.min(100, r.fillPct)}%` }}
            />
          </div>
          <span className="text-xs tabular-nums text-gray-600 dark:text-gray-300">
            {r.bookedCount}/
            <input
              type="number"
              min={0}
              defaultValue={r.capacity}
              disabled={saving}
              onBlur={(e) => {
                const v = Number(e.target.value);
                if (v !== r.capacity && v >= 0) onPatch({ capacity: v });
              }}
              className="focus:border-primary-400 w-10 rounded border border-transparent bg-transparent px-0.5 text-xs hover:border-gray-300 focus:outline-none"
            />
          </span>
          {r.waitlistCount > 0 && (
            <span className="text-xs text-amber-600">+{r.waitlistCount} wl</span>
          )}
        </div>
      </td>

      {/* Lifecycle (inline) */}
      <td className="px-3 py-3 align-top">
        <select
          className={`rounded px-1.5 py-1 text-xs font-medium ${TRIP_DATE_LIFECYCLE_BADGES[r.lifecycle]} border-0`}
          value={r.lifecycle}
          disabled={saving}
          onChange={(e) => onPatch({ lifecycle: e.target.value })}
        >
          {TRIP_DATE_LIFECYCLES.map((lc) => (
            <option key={lc} value={lc}>
              {TRIP_DATE_LIFECYCLE_LABELS[lc]}
            </option>
          ))}
        </select>
      </td>

      {/* Flights — real allotment chip (drawdown + FOC) → legacy PNR string → — */}
      <td className="px-3 py-3 align-top text-xs text-gray-600 dark:text-gray-300">
        {r.flightSignal ? (
          <Link to={`/departures/${r.id}`} className="hover:opacity-80">
            <FlightChip s={r.flightSignal} />
          </Link>
        ) : r.pnr ? (
          <span className="inline-flex items-center gap-1">
            <Plane className="h-3 w-3 text-gray-400" />
            {r.pnr}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>

      {/* Go / no-go */}
      <td className="px-3 py-3 align-top">
        <span
          className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${GO_NO_GO[r.goNoGo.level]}`}
        >
          {r.goNoGo.level === "risk" && <AlertTriangle className="h-3 w-3" />}
          {r.goNoGo.label}
        </span>
      </td>

      {/* Open velín */}
      <td className="px-4 py-3 text-right align-top">
        <Link to={`/departures/${r.id}`} className="btn-ghost btn-sm" title="Open velín">
          <ChevronRight className="h-4 w-4" />
        </Link>
      </td>
    </tr>
  );
}
